import {
  IsNotEmpty,
  IsString,
  IsOptional,
  IsObject,
  IsBoolean
} from 'class-validator';
import { IBaseResponse } from './base-response.dto';
export interface IWorkflowBlueprint {
  _id?: string;
  name: string;
  enabled: boolean;
  description?: string;
  triggerEvent: EVENT_TYPES;
  triggerFilter?: any;
  organization: string;
  createdAt?: string;
  updatedAt?: string;
  steps: IWorkflowStep[];
  history?: IWorkflowStep[];
  documentation?: string;
}

export interface IWorkflowRunError {
  category: 'internal' | 'adapter' | 'unexpected';
  message: string;
  displayMessage: string;
  /** Adapter/vendor name, only set when category === 'adapter'. */
  source?: string;
  /** false only when error-parser.ts had to fall back to string-heuristics instead of reading an AppError directly. */
  confirmed: boolean;
  /** JSON.stringify'd original detail, engineering-only, never shown to CS. */
  raw?: string;
}

// represent the workflow instance after triggering a workflow blueprint
export interface IWorkflowRun {
  _id?: string;
  blueprint: string | IWorkflowBlueprint;
  organization: string;
  triggerEvent: EVENT_TYPES;
  status: WorkflowRunStatus;
  payload: any;
  snapshot: any;
  createdAt?: string;
  updatedAt?: string;
  error?: string | IWorkflowRunError;
  isResolved?: boolean;
  isViewed?: boolean;
  reranAt?: Date;
  reranFrom?: string;
}

export interface IFailedWorkflowRunSummary {
  _id: string;
  workflowName: string;
  triggerEvent: EVENT_TYPES;
  createdAt: Date | string;
  error?: string | IWorkflowRunError;
  reranAt?: Date | string;
}

export interface IWorkflowStep {
  type: string;
  context: any;
  result?: any;
}

export interface IWorflowStepCoordinate {
  left: number;
  top: number;
  y: number;
  x: number;
  height: number;
  width: number;
}

export type WorkflowRunStatus = 'running' | 'failed' | 'completed' | 'idle';

export type WorkflowItemBaseType = 'trigger' | 'action' | 'filter';
export type WorkflowItem = IWorkflowTrigger | IWorkflowAction | IWorkflowFilter;
export interface IWorkflowTrigger extends IWorkflowItemBase {
  baseType: 'trigger';
  type: EVENT_TYPES;
}
export enum WORKFLOW_STEP_TYPES {
  trigger = 'trigger',
  send_message = 'send_message',
  condition = 'condition',
  wait = 'wait',
  created_task = 'create_task',
  update_record = 'update_record',
  conditional_split = 'conditional_split',
  adapter = 'adapter',
  crm_action = 'crm_action'
}

export interface IWorkflowAction extends IWorkflowItemBase {
  baseType: 'action';
  type: WORKFLOW_STEP_TYPES;
}
export interface IWorkflowFilter extends IWorkflowItemBase {
  baseType: 'filter';
  type: 'filter'; // todo
}

interface IWorkflowItemBase {
  id?: number;
  title?: string;
  description?: string;
  icon?: string;
  baseType: WorkflowItemBaseType;
  workflowId?: string;
  data?: { [key: string]: string };
  parent?: number;
  children?: number[];
  status: WorkflowRunStatus;
  done?: boolean;
  errors?: string[];
  hasError?: boolean;
}

/* -------------------------------------------------------------------------- */
/*                               Create new Workflow                              */
/* -------------------------------------------------------------------------- */

export class CreateWorkflowBlueprintDto {
  @IsNotEmpty()
  @IsString()
  name: string;

  @IsString()
  @IsOptional()
  description?: string;

  @IsString()
  @IsNotEmpty()
  triggerEvent: EVENT_TYPES;

  @IsString()
  @IsOptional()
  organization?: string;

  @IsOptional()
  steps: IWorkflowStep[];
}

export interface CreateWorkflowResponse extends IBaseResponse {
  workflow: IWorkflowBlueprint;
}

export class CreateWorkflowRunDto {
  id?: string;
  payload: any;
  @IsNotEmpty()
  @IsString()
  blueprint: string;
  @IsNotEmpty()
  @IsString()
  triggerEvent: EVENT_TYPES;
  @IsString()
  @IsNotEmpty()
  status: WorkflowRunStatus;
  @IsString()
  @IsOptional()
  organization?: string;
  @IsOptional()
  error?: string | IWorkflowRunError;
  @IsObject()
  snapshot: any;
}
/* -------------------------------------------------------------------------- */
/*                                   Update Workflow                              */
/* -------------------------------------------------------------------------- */

export class UpdateWorkflowDto {
  @IsNotEmpty()
  @IsString()
  name: string;

  @IsString()
  @IsOptional()
  description?: string;

  @IsNotEmpty()
  steps: IWorkflowStep[];

  @IsString()
  @IsNotEmpty()
  triggerEvent: string;

  @IsObject()
  @IsOptional()
  triggerFilter: any;

  @IsBoolean()
  enabled: boolean;
}

export interface UpdateWorkflowResponse extends IBaseResponse {
  id: string;
}

/* -------------------------------------------------------------------------- */
/*                                  Get Workflows                             */
/* -------------------------------------------------------------------------- */
export interface GetWorkflowsDto {
  triggerEvent?: string;
  name?: string;
  enabled?: boolean;
  createdAt?: Date;
}

export class GetWorkflowRunsDto {
  _id?: string;
  resourceId?: string;
  status?: WorkflowRunStatus;
  createdAt?: any;
  triggerEvent?: EVENT_TYPES;
  workflowName?: string;
  isResolved?: boolean;
  includeCount?: boolean;
  page?: number;
  size?: number;
}

export type GetWorkflowsResponse = IWorkflowBlueprint[];

export type CrmPayloadType =
  | 'appointment'
  | 'task'
  | 'lead'
  | 'form'
  | 'questionnaire'
  | 'webhook'
  | 'order'
  | 'requisition'
  | 'prescription'
  | 'fax'
  | 'cart'
  | 'customer'
  | 'message'
  | 'subscription'
  | 'lab_result'
  | 'payment';


// Extract configured param "key" values from a function config.
// If a function has no params configured, the resulting params object is `{}`.
export type ExtractParams<T> = T extends {
  params: readonly { key: infer K }[];
}
  ? K extends string
    ? K
    : never
  : never;

export type MiddlewareFunction<T extends string = string> = (
  organization: string,
  params: Record<T, any>,
  payload?: any
) => Promise<any>;

// Map a middleware config object to its handler function signatures.
export type MiddlewareConfigClass<T> = {
  [K in keyof T]: MiddlewareFunction<ExtractParams<T[K]>>
}

export interface IAdapterFunctionConfig {
  label?: string;
  pipelines?: CrmPayloadType[];
  params?: {
    label?: string;
    key: string;
    type: string;
    options?: string[];
    placeholder?: string;
  }[];
  version: number;
  passPayload?: boolean;
  middlewareHandler?: string; // Lambda function name for middleware invocation
}

export interface IAdapterEntry {
  label: string;
  functions: Record<string, IAdapterFunctionConfig>;
}

export type IAdapterConfig = Record<string, IAdapterEntry>;

type ICrmActionConfigParam = {
  key: string;
  type: string;
  label?: string;
  placeholder?: string;
};

export type ICrmActionConfig = Record<
  string,
  {
    functions: Record<
      string,
      {
        label: string;
        // will be always shown in UI
        params?: ICrmActionConfigParam[];
        // will be collapsed by default
        paramsOptional?: ICrmActionConfigParam[];
        // enable dynamic extra fields extra
        extras?: boolean;
        pipelines?: CrmPayloadType[];
        version?: number;
      }
    >;
  }
>;

export enum EVENT_TYPES {
  appointment_created = 'appointment.create',
  appointment_new = 'appointment.new',
  appointment_confirmed = 'appointment.confirmed',
  appointment_waiting = 'appointment.waiting',
  appointment_in_progress = 'appointment.in-progress',
  appointment_cancelled = 'appointment.cancelled',
  appointment_completed = 'appointment.completed',
  appointment_no_show = 'appointment.no-show',
  appointment_closed = 'appointment.closed',
  appointment_updated = 'appointment.updated',
  lab_result_created = 'lab_result.created',
  lab_result_verified = 'lab_result.verified',
  lead_created = 'lead.created',
  lead_converted = 'lead.converted',
  lead_lost = 'lead.lost',
  cart_purchased = 'cart.purchased',
  customer_signup = 'customer.signup',
  customer_created = 'customer.created',
  customer_qnr_assigned = 'customer.qnr-assigned',
  customer_updated = 'customer.updated',
  customer_address_updated = 'customer.address_updated',
  task_created = 'task.created',
  task_completed = 'task.completed',
  task_cancelled = 'task.cancelled',
  form_submitted = 'form.submitted',
  webhook_event = 'webhook.event',
  requisition_created = 'requisition.created',
  prescription_created = 'prescription.created',
  prescription_send = 'prescription.send',
  prescription_fulfillment = 'prescription.fulfillment',
  prescription_voided = 'prescription.voided',
  fax_received = 'fax.received',
  order_created = 'order.created',
  order_subscription_cycle = 'order.subscription_cycle',
  order_payment = 'order.awaiting_payment',
  order_processing = 'order.processing',
  awaiting_requirements = 'order.awaiting_requirements',
  order_awaiting_results = 'order.awaiting_results',
  awaiting_fulfillment = 'order.awaiting_fulfillment',
  awaiting_script = 'order.awaiting_script',
  awaiting_requisition = 'order.awaiting_requisition',
  awaiting_shipment = 'order.awaiting_shipment',
  order_cancelled = 'order.cancelled',
  order_shipped = 'order.shipped',
  order_status_updated = 'order.status_updated',
  order_shipping_address_updated = 'order.shipping_address_updated',
  order_rejected = 'order.rejected',
  order_completed = 'order.completed',
  questionnaire_created = 'questionnaire.created',
  questionnaire_started = 'questionnaire.started',
  questionnaire_submitted = 'questionnaire.submitted',
  questionnaire_reviewed = 'questionnaire.reviewed',
  questionnaire_rejected = 'questionnaire.rejected',
  cart_checkout = 'cart.checkout',
  message_inbound = 'message.inbound',
  message_outbound = 'message.outbound',
  subscription_created = 'subscription.created',
  subscription_active = 'subscription.active',
  subscription_paused = 'subscription.paused',
  subscription_renewal_updated = 'subscription.renewal_updated',
  subscription_cancelled = 'subscription.cancelled',
  subscription_ended = 'subscription.ended',
  subscription_upcoming_renewal = 'subscription.upcoming-renewal',
  subscription_renewed = 'subscription.renewed',
  order_payment_adjudicated = 'order.payment_adjudicated',
  order_payment_successful = 'order.payment_successful',
  order_payment_failed = 'order.payment_failed',
  order_lab_out_for_delivery = 'order.lab_out_for_delivery',
  order_lab_with_customer = 'order.lab_with_customer',
  order_lab_transit_lab = 'order.lab_transit_lab',
  order_lab_delivered_to_lab = 'order.lab_delivered_to_lab'
}
Object.freeze(EVENT_TYPES);
export interface IEvent {
  // EventBridge event
  version: '0';
  id: string; // '6908d5be-7ed5-30c2-5c5b-cd107b2139cb',
  'detail-type': EVENT_TYPES;
  source: string; // crm-api@<org>
  account: '085611246467';
  time: string;
  region: 'ca-central-1';
  resources: string[];
  detail: any;
}
